/**
 * printerService — abstraction layer around label printing.
 *
 * Purpose: shield UI from the concrete transport (browser download, browser print,
 * Web Bluetooth to NIIMBOT B1) so we can add direct Bluetooth printing later
 * without touching the pages that call this module.
 *
 * All methods return Promise<{ ok: boolean, message?: string }>.
 */

import { API_BASE } from "@/lib/api";

const authHeaders = () => {
  const t = typeof window !== "undefined" ? localStorage.getItem("1ai_token") : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Fetch the label PNG blob (server-rendered, print-perfect).
 *
 * Backward-compat: `resource` may be a plain customer id string (legacy) OR
 * an object `{ scope: 'customers'|'stands', id: '...' }` for the new
 * physical-stand inventory. When called with a string we assume 'customers'.
 */
function _resolve(resource) {
  if (typeof resource === "string") return { scope: "customers", id: resource };
  return resource;
}

export async function fetchLabelBlob(resource, { sizeMm = 50, dpi = 300 } = {}) {
  const { scope, id } = _resolve(resource);
  const url = `${API_BASE}/${scope}/${id}/label.png?size_mm=${sizeMm}&dpi=${dpi}`;
  const r = await fetch(url, { credentials: "include", headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.blob();
}

/**
 * Trigger a browser download of the label PNG.
 */
export async function downloadPng(resource, filename, opts = {}) {
  const blob = await fetchLabelBlob(resource, opts);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
  return { ok: true };
}

/**
 * Open the browser print dialog on a hidden iframe rendered at the exact
 * physical size (50 × 50 mm by default). Works for any label printer that has
 * a system driver — including the NIIMBOT B1 in Wi-Fi / USB mode.
 */
export async function browserPrint(resource, { sizeMm = 50, dpi = 300 } = {}) {
  const blob = await fetchLabelBlob(resource, { sizeMm, dpi });
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(new Error("read failed"));
    fr.onload = () => resolve(fr.result);
    fr.readAsDataURL(blob);
  });

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Label</title>
    <style>
      @page { size: ${sizeMm}mm ${sizeMm}mm; margin: 0; }
      html, body { margin: 0; padding: 0; background: #fff; }
      img { display: block; width: ${sizeMm}mm; height: ${sizeMm}mm; }
    </style></head><body><img id="lbl" src="${dataUrl}" /></body></html>`);
  doc.close();

  await new Promise((r) => setTimeout(r, 250));
  try {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
  } finally {
    setTimeout(() => iframe.remove(), 1500);
  }
  return { ok: true };
}

/**
 * NIIMBOT B1 Bluetooth transport — placeholder.
 * Direct browser-to-printer over Web Bluetooth requires the NIIMBOT protocol
 * (bitmap chunking, cmd framing). It is intentionally stubbed here so the UI
 * can already surface the button and route to it once implemented.
 */
export async function sendToBluetooth() {
  return {
    ok: false,
    message:
      "Direkter Bluetooth-Druck an NIIMBOT B1 ist vorbereitet und folgt in einem der nächsten Updates.",
  };
}

/**
 * True if the browser exposes Web Bluetooth (Chrome / Edge on desktop + Android).
 */
export function isBluetoothSupported() {
  return typeof navigator !== "undefined" && "bluetooth" in navigator;
}
